<template>
  <div 
    class="timeline-tracks" 
    @wheel="handleWheel"
    @mousemove="onDrag"
    @mouseup="endDrag"
    @mouseleave="endDrag"
  >
    <!-- Header with Ticks -->
    <div class="timeline-header" ref="headerRef" @mousedown="startPlayheadDrag">
      <div class="sidebar-corner" :style="{ width: sidebarWidth + 'px' }">
        <div class="sidebar-resizer" @mousedown.stop="startSidebarResize"></div>
      </div>
      <div id="ticks" ref="ticksRef" :style="{ width: totalWidth + 'px' }">
        <div 
          v-for="s in visibleTicks" 
          :key="s" 
          v-memo="[pxPerSec, maxSeconds]"
          class="tick" 
          :style="{ left: s * pxPerSec + 'px' }"
        >
          {{ formatTime(s) }}
        </div>
      </div>
    </div>

    <!-- Viewport for Tracks -->
    <div 
      class="timeline-viewport" 
      ref="viewport" 
      @mousedown="startDrag"
      @scroll="syncScroll"
    >
      <div id="timeline-track" :style="{ width: (totalWidth + sidebarWidth) + 'px' }">
        <!-- Playhead -->
        <div 
          id="playhead" 
          :style="{
            left: sidebarWidth + 'px',
            transform: `translate3d(${playheadPos * props.pxPerSec}px, 0, 0)`
          }"
          @mousedown.stop="startPlayheadDrag"
        >
          <div class="playhead-hitzone"></div>
        </div>

        <!-- Selection Draft Overlay -->
        <div 
          v-if="isSelecting"
          class="selection-draft" 
          :style="{ 
            left: (Math.min(selectionStart || 0, selectionEnd || 0) * props.pxPerSec + sidebarWidth) + 'px', 
            width: (Math.abs((selectionStart || 0) - (selectionEnd || 0)) * props.pxPerSec) + 'px' 
          }"
        ></div>

        <!-- Selected Segments Overlay -->
        <template v-for="(seg, idx) in selectedSegments" :key="seg.id || 'seg-' + idx">
          <div 
            v-if="!seg.transcription_id || seg.transcription_id === timelineData?.transcription_id"
            class="selected-segment"
            :style="{ 
              left: (seg.start * props.pxPerSec + sidebarWidth) + 'px', 
              width: ((seg.end - seg.start) * props.pxPerSec) + 'px' 
            }"
          >
            <div class="handle-left" @mousedown.stop="startResize($event, idx, 'left')"></div>
            <div class="seg-label">{{ (seg.end - seg.start).toFixed(1) }}s</div>
            <v-icon size="x-small" class="delete-icon" @click.stop="emit('remove-segment', idx)">mdi-close-circle</v-icon>
            <div class="handle-right" @mousedown.stop="startResize($event, idx, 'right')"></div>
          </div>
        </template>

        <!-- Commodity Track (Global) -->
        <div v-if="isTrackGroupVisible('commodity') && timelineData?.commodities && timelineData.commodities.length > 0" class="track-container commodity-track">
          <div class="track-sidebar" :style="{ width: sidebarWidth + 'px' }">
            <div class="profile-header filterable-profile-header">
              <v-icon size="x-small" color="orange" class="mr-1">mdi-shopping</v-icon>
              <span class="profile-text" style="color: #ff9800;">商品讲解</span>
              <span class="filter-count">{{ visibleCommodities.length }}/{{ commodities.length }}</span>
              <v-menu :close-on-content-click="false" location="end">
                <template v-slot:activator="{ props: menuProps }">
                  <v-btn
                    v-bind="menuProps"
                    icon="mdi-filter-variant"
                    size="x-small"
                    density="compact"
                    variant="text"
                    :color="commodityFilterActive ? 'orange' : 'grey'"
                    title="过滤显示的商品"
                    @mousedown.stop
                    @click.stop
                  ></v-btn>
                </template>
                <v-card class="track-filter-menu" theme="dark" @mousedown.stop @click.stop>
                  <div class="filter-menu-header">
                    <span>显示商品</span>
                    <div>
                      <v-btn size="x-small" variant="text" color="primary" @click="selectAllCommodities">全选</v-btn>
                      <v-btn size="x-small" variant="text" color="grey" @click="visibleCommodityNames = []">清空</v-btn>
                    </div>
                  </div>
                  <v-divider></v-divider>
                  <div class="filter-options">
                    <label v-for="name in commodityNames" :key="name" class="filter-option">
                      <v-checkbox-btn
                        :model-value="visibleCommodityNames.includes(name)"
                        color="orange"
                        density="compact"
                        @update:model-value="toggleCommodity(name, $event)"
                      ></v-checkbox-btn>
                      <span :title="name">{{ name }}</span>
                    </label>
                  </div>
                </v-card>
              </v-menu>
            </div>
            <div class="sidebar-resizer" @mousedown.stop="startSidebarResize"></div>
          </div>
          
          <div class="track-content" :style="{ width: totalWidth + 'px' }">
            <div v-if="visibleCommodities.length === 0" class="track-empty-state commodity-empty-state">
              当前筛选未显示任何商品
            </div>
            <div 
              v-for="(comm, idx) in visibleCommodities"
              :key="'comm-' + idx"
              class="commodity-block"
              :style="{
                left: (comm.startSec * pxPerSec) + 'px',
                width: ((comm.endSec - comm.startSec) * pxPerSec) + 'px',
                backgroundColor: getCommodityColorStyle(comm.name).backgroundColor,
                borderColor: getCommodityColorStyle(comm.name).borderColor
              }"
              @click="handleContentClick($event, { start: comm.startSec, end: comm.endSec })"
            >
              <v-menu 
                location="top" 
                open-on-hover 
                open-delay="200" 
                content-class="comm-tooltip"
                :close-on-content-click="false"
                offset="2"
              >
                <template v-slot:activator="{ props: menuProps }">
                  <div class="comm-inner" v-bind="menuProps">
                    <span class="comm-name" :style="{ color: getCommodityColorStyle(comm.name).color }">{{ comm.name }}</span>
                  </div>
                </template>
                <div class="timeline-tooltip-card" @click.stop @mousedown.stop>
                  <div class="tooltip-content d-flex align-center">
                    <v-avatar rounded size="40" class="mr-2" v-if="comm.thumb" @click.stop="previewImage(comm.thumb)" style="cursor: pointer;">
                      <v-img :src="comm.thumb"></v-img>
                    </v-avatar>
                    <div class="tooltip-copy">
                      <div class="text-caption font-weight-bold" style="color: #fff;">{{ comm.name }}</div>
                      <div class="text-caption text-warning" v-if="comm.price">销量/价格指数: {{ comm.price }}</div>
                      <div class="text-caption" style="color: #ccc;">Shift+点击 快速提取此段</div>
                    </div>
                  </div>
                  <v-divider class="my-1"></v-divider>
                  <div class="tooltip-filter-actions">
                    <v-btn size="x-small" variant="tonal" color="orange" @click="showOnlyCommodity(comm.name)">只看此商品</v-btn>
                    <v-btn size="x-small" variant="text" color="grey-lighten-1" @click="hideCommodity(comm.name)">隐藏此商品</v-btn>
                    <v-btn size="x-small" variant="text" color="primary" @click="selectAllCommodities">显示全部</v-btn>
                  </div>
                </div>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- Speaker Diarization Track -->
        <div v-if="isTrackGroupVisible('speaker') && timelineData" class="track-container speaker-track">
          <div class="track-sidebar" :style="{ width: sidebarWidth + 'px' }">
            <div class="profile-header filterable-profile-header">
              <v-icon size="x-small" color="light-green-accent-2" class="mr-1">mdi-account-voice</v-icon>
              <span class="profile-text speaker-track-title">说话人</span>
              <span class="filter-count">{{ visibleSpeakerIds.length }}/{{ speakerIds.length }}</span>
              <v-menu v-if="speakerIds.length > 0" :close-on-content-click="false" location="end">
                <template v-slot:activator="{ props: menuProps }">
                  <v-btn
                    v-bind="menuProps"
                    icon="mdi-filter-variant"
                    size="x-small"
                    density="compact"
                    variant="text"
                    :color="speakerFilterActive ? 'light-green-accent-2' : 'grey'"
                    title="过滤显示的说话人"
                    @mousedown.stop
                    @click.stop
                  ></v-btn>
                </template>
                <v-card class="track-filter-menu" theme="dark" @mousedown.stop @click.stop>
                  <div class="filter-menu-header">
                    <span>显示说话人</span>
                    <div>
                      <v-btn size="x-small" variant="text" color="primary" @click="selectAllSpeakers">全选</v-btn>
                      <v-btn size="x-small" variant="text" color="grey" @click="visibleSpeakerIds = []">清空</v-btn>
                    </div>
                  </div>
                  <v-divider></v-divider>
                  <div class="filter-options">
                    <label v-for="speaker in speakerIds" :key="speaker" class="filter-option">
                      <v-checkbox-btn
                        :model-value="visibleSpeakerIds.includes(speaker)"
                        color="light-green-accent-2"
                        density="compact"
                        @update:model-value="toggleSpeaker(speaker, $event)"
                      ></v-checkbox-btn>
                      <span>{{ speaker }}</span>
                    </label>
                  </div>
                </v-card>
              </v-menu>
            </div>
            <div class="sidebar-resizer" @mousedown.stop="startSidebarResize"></div>
          </div>

          <div class="track-content" :style="{ width: totalWidth + 'px' }">
            <div v-if="speakerSegments.length === 0" class="track-empty-state">
              <v-icon size="x-small" class="mr-1">mdi-information-outline</v-icon>
              {{ speakerTimelineAvailable ? '暂无说话人识别数据' : '服务端未返回说话人数据，请更新或重启服务' }}
            </div>
            <div v-else-if="visibleSpeakerSegments.length === 0" class="track-empty-state">
              当前筛选未显示任何说话人
            </div>
            <div
              v-for="(segment, idx) in visibleSpeakerSegments"
              :key="'speaker-' + idx"
              class="speaker-block"
              :style="speakerBlockStyle(segment)"
              @click="handleContentClick($event, { start: segment.startSec, end: segment.endSec })"
            >
              <v-menu
                location="top"
                open-on-hover
                open-delay="200"
                content-class="comm-tooltip"
                :close-on-content-click="false"
                offset="2"
              >
                <template v-slot:activator="{ props: menuProps }">
                  <div class="speaker-block-inner" v-bind="menuProps">
                    <span class="speaker-name">{{ segment.speaker }}</span>
                    <span class="speaker-text">{{ segment.text }}</span>
                  </div>
                </template>
                <div class="timeline-tooltip-card speaker-tooltip-card" @click.stop @mousedown.stop>
                  <div class="speaker-tooltip-heading">
                    <v-icon size="small" color="light-green-accent-2" class="mr-1">mdi-account-voice</v-icon>
                    <strong>{{ segment.speaker }}</strong>
                    <span>{{ formatSegmentRange(segment) }}</span>
                  </div>
                  <div class="speaker-tooltip-text">{{ segment.text || '暂无转录文本' }}</div>
                  <v-divider class="my-1"></v-divider>
                  <div class="tooltip-filter-actions">
                    <v-btn size="x-small" variant="tonal" color="light-green-accent-2" @click="showOnlySpeaker(segment.speaker)">只看此说话人</v-btn>
                    <v-btn size="x-small" variant="text" color="grey-lighten-1" @click="hideSpeaker(segment.speaker)">隐藏此说话人</v-btn>
                    <v-btn size="x-small" variant="text" color="primary" @click="selectAllSpeakers">显示全部</v-btn>
                  </div>
                </div>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- Tracks -->
        <template v-if="isTrackGroupVisible('visual')">
          <div
            v-for="(history, profileId) in (timelineData?.tracks || {})"
            :key="profileId"
            v-memo="[history, pxPerSec, sidebarWidth, highlights[profileId], clothingBlocks[profileId]]"
            class="track-container"
            :style="{ height: trackHeight + 'px' }"
          >
            <!-- Track Sidebar (Sticky) -->
            <div class="track-sidebar" :style="{ width: sidebarWidth + 'px' }">
              <div class="profile-header">
                <v-icon size="x-small" color="cyan-accent-3" class="mr-1">mdi-account-circle</v-icon>
                <span class="profile-text">{{ profileId }}</span>
              </div>
              <div class="track-row-labels">
                <div class="row-label-item label-upper">上装</div>
                <div class="row-label-item label-lower">下装</div>
              </div>
              <div class="sidebar-resizer" @mousedown.stop="startSidebarResize"></div>
            </div>

            <div class="track-content" :style="{ width: totalWidth + 'px' }">
              <!-- Highlights from Search -->
              <div v-if="highlights[profileId]">
                <div
                  v-for="(h, idx) in highlights[profileId]"
                  :key="idx"
                  class="highlight-mark"
                  :style="{ left: h.sec * pxPerSec + 'px', width: pxPerSec + 'px' }"
                ></div>
              </div>

            <!-- Color Blocks -->
            <div class="color-row upper-row">
              <div 
                v-for="(block, idx) in (clothingBlocks[profileId]?.upper || [])"
                :key="'u' + idx"
                class="color-block"
                :style="getBlockStyle(block)"
                :title="getTooltip(block.colors)"
                @click="handleContentClick($event, { start: block.startSec, end: block.endSec })"
              >
                <div 
                  v-for="(c, cidx) in block.colors" 
                  :key="cidx"
                  :style="{ flex: c.ratio, backgroundColor: bgrToHex(c.bgr) }"
                ></div>
              </div>
            </div>

            <div class="color-row lower-row">
              <div 
                v-for="(block, idx) in (clothingBlocks[profileId]?.lower || [])"
                :key="'l' + idx"
                class="color-block"
                :style="getBlockStyle(block)"
                :title="getTooltip(block.colors)"
                @click="handleContentClick($event, { start: block.startSec, end: block.endSec })"
              >
                <div 
                  v-for="(c, cidx) in block.colors" 
                  :key="cidx"
                  :style="{ flex: c.ratio, backgroundColor: bgrToHex(c.bgr) }"
                ></div>
              </div>
            </div>
            </div>
            </div>
        </template>

        <!-- Placeholder Track when no data -->
        <div v-if="isTrackGroupVisible('visual') && !(timelineData?.tracks && Object.keys(timelineData.tracks).length > 0) && !(timelineData?.commodities && timelineData.commodities.length > 0)" class="track-container placeholder-track">
          <div class="track-sidebar" :style="{ width: sidebarWidth + 'px' }">
            <div class="profile-header">
              <v-icon size="x-small" color="grey" class="mr-1">mdi-video-outline</v-icon>
              <span class="profile-text" style="color: #aaa;">主视频轴</span>
            </div>
            <div class="sidebar-resizer" @mousedown.stop="startSidebarResize"></div>
          </div>
          <div class="track-content" :style="{ width: totalWidth + 'px' }">
            <div class="placeholder-bg"></div>
            </div>
        </div>

        <!-- Audio Track -->
        <div v-if="isTrackGroupVisible('audio') && audioTimeline.length > 0" class="track-container audio-track">
          <div class="track-sidebar" :style="{ width: sidebarWidth + 'px' }">
            <div class="profile-header metric-profile-header">
              <v-icon size="x-small" color="cyan" class="mr-1">mdi-volume-high</v-icon>
              <div class="metric-title-stack">
                <span class="profile-text" style="color: #00bcd4;">相对响度</span>
                <span v-if="loudnessSummary" class="metric-mean audio-mean">
                  均值 {{ Number(loudnessSummary.mean_rms_dbfs).toFixed(1) }} dBFS
                </span>
              </div>
            </div>
            <div class="sidebar-resizer" @mousedown.stop="startSidebarResize"></div>
          </div>
          
          <div class="track-content" :style="{ width: totalWidth + 'px' }">
            <TimelineMetricChart
              kind="loudness"
              :points="audioTimeline"
              :px-per-sec="pxPerSec"
              :max-seconds="maxSeconds"
              :mean-value="Number(loudnessSummary?.mean_rms_dbfs)"
              @seek="seekFromMetric"
            />
          </div>
        </div>

        <!-- Speech Rate Track -->
        <div v-if="isTrackGroupVisible('audio') && speechRateTimeline.length > 0" class="track-container speech-rate-track">
          <div class="track-sidebar" :style="{ width: sidebarWidth + 'px' }">
            <div class="profile-header metric-profile-header">
              <v-icon size="x-small" color="deep-purple-accent-1" class="mr-1">mdi-speedometer</v-icon>
              <div class="metric-title-stack">
                <span class="profile-text" style="color: #b39ddb;">相对语速</span>
                <span v-if="speechRateSummary" class="metric-mean speech-rate-mean">
                  均值 {{ Number(speechRateSummary.mean_window_speech_rate_cpm).toFixed(0) }} 字/分
                </span>
              </div>
            </div>
            <div class="sidebar-resizer" @mousedown.stop="startSidebarResize"></div>
          </div>

          <div class="track-content" :style="{ width: totalWidth + 'px' }">
            <TimelineMetricChart
              kind="speech-rate"
              :points="speechRateTimeline"
              :px-per-sec="pxPerSec"
              :max-seconds="maxSeconds"
              :step-seconds="speechRateStepSeconds"
              :mean-value="Number(speechRateSummary?.mean_window_speech_rate_cpm)"
              @seek="seekFromMetric"
            />
          </div>
        </div>

        <!-- Scroll Spacer -->
        <div class="scroll-spacer"></div>

        <!-- Click Layer -->
        <div id="click-layer" :style="{ left: sidebarWidth + 'px', width: totalWidth + 'px' }" @click.stop="onTimelineClick"></div>
      </div>
    </div>

    <!-- Zoom Controls -->
    <div class="zoom-controls">
      <v-btn icon="mdi-minus" size="x-small" density="comfortable" variant="text" @click="zoom(0.9)"></v-btn>
      <span class="zoom-text">{{ pxPerSec.toFixed(0) }}px/s</span>
      <v-btn icon="mdi-plus" size="x-small" density="comfortable" variant="text" @click="zoom(1.1)"></v-btn>
    </div>

    <!-- Image Preview Dialog -->
    <v-dialog v-model="showLargeImage" max-width="600">
      <v-card v-if="largeImageUrl" class="pa-1" theme="dark" style="overflow: hidden;">
        <v-img :src="largeImageUrl" width="100%" cover max-height="80vh"></v-img>
        <v-card-actions class="justify-end py-1">
          <v-btn variant="text" size="small" @click="showLargeImage = false">关闭</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import TimelineMetricChart from './TimelineMetricChart.vue'

const props = defineProps({
  timelineData: Object,
  pxPerSec: Number,
  maxSeconds: Number,
  playheadPos: Number,
  highlights: Object,
  selectedSegments: {
    type: Array,
    default: () => []
  },
  autoScroll: {
    type: Boolean,
    default: true
  },
  visibleTrackGroups: {
    type: Array,
    default: () => ['commodity', 'speaker', 'visual', 'audio']
  }
})

const isTrackGroupVisible = group => props.visibleTrackGroups.includes(group)

const emit = defineEmits([
  'update:pxPerSec', 
  'click-sec', 
  'scroll-sync', 
  'add-segment', 
  'remove-segment', 
  'update-segment',
  'clear-segments'
])

const viewport = ref(null)

// Sidebar width management
const sidebarWidth = ref(130)
const isResizingSidebar = ref(false)

const startSidebarResize = (e) => {
  isResizingSidebar.value = true
  document.addEventListener('mousemove', onSidebarResize)
  document.addEventListener('mouseup', stopSidebarResize)
  e.preventDefault()
}

const onSidebarResize = (e) => {
  if (!isResizingSidebar.value) return
  // Find the container left position
  const rect = viewport.value.getBoundingClientRect()
  const newWidth = e.clientX - rect.left
  sidebarWidth.value = Math.min(300, Math.max(80, newWidth))
}

const stopSidebarResize = () => {
  isResizingSidebar.value = false
  document.removeEventListener('mousemove', onSidebarResize)
  document.removeEventListener('mouseup', stopSidebarResize)
}

// Watch playhead to auto-scroll
watch(() => props.playheadPos, (newPos) => {
  if (!props.autoScroll || !viewport.value) return
  
  const x = newPos * props.pxPerSec
  const scrollLeft = viewport.value.scrollLeft
  const width = viewport.value.clientWidth - sidebarWidth.value
  
  // If playhead is outside the visible area (with some margin), scroll to it
  if (x < scrollLeft || x > scrollLeft + width - 50) {
    viewport.value.scrollLeft = Math.max(0, x - width / 2)
  }
})

const headerRef = ref(null)
const ticksRef = ref(null)
const trackHeight = 60
// Removed static sidebarWidth

const totalWidth = computed(() => props.maxSeconds * props.pxPerSec)

const loudnessResult = computed(() => props.timelineData?.components?.loudness?.result || null)
const loudnessSummary = computed(() => loudnessResult.value?.summary || null)
const audioTimeline = computed(() => {
  return loudnessResult.value?.timeline || props.timelineData?.audio_data || []
})

const speechRateResult = computed(() => props.timelineData?.components?.speech_rate?.result || null)
const speechRateSummary = computed(() => speechRateResult.value?.summary || null)
const speechRateTimeline = computed(() => speechRateResult.value?.timeline || [])
const speechRateStepSeconds = computed(() => Number(speechRateResult.value?.step_seconds) || 1)
const seekFromMetric = sec => emit('click-sec', { sec, profileId: null, node: null })

const speakerSegments = computed(() => props.timelineData?.speaker_timeline?.segments || [])
const speakerIds = computed(() => {
  const declared = props.timelineData?.speaker_timeline?.speakers || []
  const fromSegments = speakerSegments.value.map(segment => segment.speaker).filter(Boolean)
  return [...new Set([...declared, ...fromSegments])].map(String).sort()
})
const visibleSpeakerIds = ref([])
const visibleSpeakerSegments = computed(() => {
  const visible = new Set(visibleSpeakerIds.value)
  return speakerSegments.value.filter(segment => visible.has(String(segment.speaker)))
})
const speakerFilterActive = computed(() => visibleSpeakerIds.value.length < speakerIds.value.length)
const selectAllSpeakers = () => {
  visibleSpeakerIds.value = [...speakerIds.value]
}
const toggleSpeaker = (speaker, checked) => {
  visibleSpeakerIds.value = checked
    ? [...new Set([...visibleSpeakerIds.value, speaker])]
    : visibleSpeakerIds.value.filter(item => item !== speaker)
}
const showOnlySpeaker = speaker => {
  visibleSpeakerIds.value = [String(speaker)]
}
const hideSpeaker = speaker => {
  visibleSpeakerIds.value = visibleSpeakerIds.value.filter(item => item !== String(speaker))
}
watch(speakerIds, selectAllSpeakers, { immediate: true })

const speakerTimelineAvailable = computed(() => {
  return Object.prototype.hasOwnProperty.call(props.timelineData || {}, 'speaker_timeline')
})
const SPEAKER_COLORS = [
  { bg: 'rgba(139, 195, 74, 0.28)', border: '#9ccc65', text: '#dcedc8' },
  { bg: 'rgba(255, 193, 7, 0.25)', border: '#ffd54f', text: '#fff8e1' },
  { bg: 'rgba(3, 169, 244, 0.25)', border: '#4fc3f7', text: '#e1f5fe' },
  { bg: 'rgba(236, 64, 122, 0.25)', border: '#f06292', text: '#fce4ec' },
  { bg: 'rgba(171, 71, 188, 0.25)', border: '#ba68c8', text: '#f3e5f5' },
  { bg: 'rgba(255, 112, 67, 0.25)', border: '#ff8a65', text: '#fbe9e7' }
]

const speakerColor = speaker => {
  const index = Math.max(0, speakerIds.value.indexOf(speaker))
  return SPEAKER_COLORS[index % SPEAKER_COLORS.length]
}

const speakerBlockStyle = segment => {
  const color = speakerColor(segment.speaker)
  return {
    left: `${Number(segment.startSec) * props.pxPerSec}px`,
    width: `${Math.max(0, Number(segment.endSec) - Number(segment.startSec)) * props.pxPerSec}px`,
    backgroundColor: color.bg,
    borderColor: color.border,
    color: color.text
  }
}

const formatSegmentRange = segment => {
  const start = Number(segment.startSec).toFixed(2)
  const end = Number(segment.endSec).toFixed(2)
  return `${start}s - ${end}s`
}

// Selection & Dragging state
const isSelecting = ref(false)
const selectionStart = ref(null)
const selectionEnd = ref(null)
const isResizing = ref(false)
const isDraggingPlayhead = ref(false)
const resizingIdx = ref(-1)
const resizingSide = ref('')
let isDragging = false

// Image preview state
const showLargeImage = ref(false)
const largeImageUrl = ref('')
const previewImage = (url) => {
  if (!url) return
  largeImageUrl.value = url
  showLargeImage.value = true
}

let hasMoved = false
let startX = 0
let initialScrollLeft = 0

const syncScroll = () => {
  if (headerRef.value && viewport.value) {
    headerRef.value.scrollLeft = viewport.value.scrollLeft
  }
}

const tickInterval = computed(() => {
  const minSpacing = 60
  const intervals = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600]
  for (let i of intervals) {
    if (i * props.pxPerSec >= minSpacing) return i
  }
  return 600
})

const visibleTicks = computed(() => {
  const ticks = []
  const interval = tickInterval.value
  for (let s = 0; s <= props.maxSeconds; s += interval) {
    ticks.push(s)
  }
  return ticks
})

const formatTime = (s) => `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}`

const handleContentClick = (e, segment) => {
  if (e.shiftKey) {
    emit('add-segment', segment)
    e.stopPropagation()
  } else {
    // 正常点击，转发给背景的 onTimelineClick
    onTimelineClick(e)
  }
}

const bgrToHex = (bgr) => {
  if (!bgr || bgr.length < 3) return '#000'
  return "#" + (1 << 24 | bgr[2] << 16 | bgr[1] << 8 | bgr[0]).toString(16).slice(1)
}

const PASTEL_COLORS = [
  { bg: 'rgba(33, 150, 243, 0.2)', border: 'rgba(33, 150, 243, 0.6)', text: '#90CAF9' },  // 蓝色
  { bg: 'rgba(76, 175, 80, 0.2)',  border: 'rgba(76, 175, 80, 0.6)',  text: '#A5D6A7' },  // 绿色
  { bg: 'rgba(255, 152, 0, 0.2)',  border: 'rgba(255, 152, 0, 0.6)',  text: '#FFCC80' },  // 橙色
  { bg: 'rgba(156, 39, 176, 0.2)', border: 'rgba(156, 39, 176, 0.6)', text: '#CE93D8' }, // 紫色
  { bg: 'rgba(0, 188, 212, 0.2)',  border: 'rgba(0, 188, 212, 0.6)',  text: '#80DEEA' },  // 青色
  { bg: 'rgba(233, 30, 99, 0.2)',  border: 'rgba(233, 30, 99, 0.6)',  text: '#F48FB1' },  // 粉色
  { bg: 'rgba(121, 85, 72, 0.3)',  border: 'rgba(121, 85, 72, 0.7)',  text: '#BCAAA4' },  // 棕色
  { bg: 'rgba(0, 150, 136, 0.2)',  border: 'rgba(0, 150, 136, 0.6)',  text: '#80CBC4' },  // 青碧
  { bg: 'rgba(63, 81, 181, 0.2)',  border: 'rgba(63, 81, 181, 0.6)',  text: '#9FA8DA' },  // 靛蓝
  { bg: 'rgba(255, 193, 7, 0.2)',  border: 'rgba(255, 193, 7, 0.6)',  text: '#FFE082' },  // 琥珀
  { bg: 'rgba(205, 220, 57, 0.2)', border: 'rgba(205, 220, 57, 0.6)', text: '#E6EE9C' },  // 青柠
  { bg: 'rgba(103, 58, 183, 0.2)', border: 'rgba(103, 58, 183, 0.6)', text: '#B39DDB' }, // 深紫
  { bg: 'rgba(244, 67, 54, 0.2)',  border: 'rgba(244, 67, 54, 0.6)',  text: '#EF9A9A' },  // 红色
  { bg: 'rgba(96, 125, 139, 0.2)', border: 'rgba(96, 125, 139, 0.6)', text: '#B0BEC5' }, // 蓝灰
  { bg: 'rgba(255, 87, 34, 0.2)',  border: 'rgba(255, 87, 34, 0.6)',  text: '#FFAB91' },  // 深橙
]

const commodities = computed(() => props.timelineData?.commodities || [])
const commodityNames = computed(() => {
  return [...new Set(commodities.value.map(item => item.name).filter(Boolean))].map(String).sort()
})
const visibleCommodityNames = ref([])
const visibleCommodities = computed(() => {
  const visible = new Set(visibleCommodityNames.value)
  return commodities.value.filter(item => visible.has(String(item.name)))
})
const commodityFilterActive = computed(() => visibleCommodityNames.value.length < commodityNames.value.length)
const selectAllCommodities = () => {
  visibleCommodityNames.value = [...commodityNames.value]
}
const toggleCommodity = (name, checked) => {
  visibleCommodityNames.value = checked
    ? [...new Set([...visibleCommodityNames.value, name])]
    : visibleCommodityNames.value.filter(item => item !== name)
}
const showOnlyCommodity = name => {
  visibleCommodityNames.value = [String(name)]
}
const hideCommodity = name => {
  visibleCommodityNames.value = visibleCommodityNames.value.filter(item => item !== String(name))
}
watch(commodityNames, selectAllCommodities, { immediate: true })

// 避免不同商品颜色过于接近，进行硬性去重映射
const commodityColorMap = computed(() => {
  const map = {}
  const items = props.timelineData?.commodities || []
  // 获取唯一名称并排序，保证颜色分配相对稳定
  const uniqueNames = [...new Set(items.map(c => c.name).filter(Boolean))].sort()
  
  uniqueNames.forEach((name, i) => {
    map[name] = PASTEL_COLORS[i % PASTEL_COLORS.length]
  })
  return map
})

const getCommodityColorStyle = (name) => {
  if (!name) return {}
  const color = commodityColorMap.value[name]
  if (!color) {
    // 降级使用哈希
    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash) % PASTEL_COLORS.length
    const fbColor = PASTEL_COLORS[index]
    return {
      backgroundColor: fbColor.bg,
      borderColor: fbColor.border,
      color: fbColor.text
    }
  }
  return {
    backgroundColor: color.bg,
    borderColor: color.border,
    color: color.text
  }
}

const getBlocks = (history, colorKey) => {
  const invalidNames = new Set(['错误', '分析失败', '未检测到', '未检测到 (疑似裸露皮肤)', '未知'])
  const points = (history || [])
    .filter(d => Array.isArray(d[colorKey]) && d[colorKey].some(c => !invalidNames.has(c.name)))
    .map(d => {
      const colors = d[colorKey].filter(c => !invalidNames.has(c.name))
      const signature = colors
        .filter(c => Number(c.ratio || 0) >= 0.15)
        .map(c => c.name)
        .sort()
        .join('|') || colors[0]?.name || ''
      return { ...d, colors, signature }
    })
    .sort((a, b) => a.sec - b.sec)

  // 5 个采样点内对颜色类别做多数投票，抑制单帧 KMeans 抖动。
  const stabilized = points.map((point, index) => {
    const votes = new Map()
    for (let i = Math.max(0, index - 2); i <= Math.min(points.length - 1, index + 2); i++) {
      if (Math.abs(points[i].sec - point.sec) > 3) continue
      const key = points[i].signature
      const vote = votes.get(key) || { count: 0, colors: points[i].colors }
      vote.count += 1
      votes.set(key, vote)
    }
    const winner = [...votes.entries()].sort((a, b) => b[1].count - a[1].count)[0]
    return winner
      ? { ...point, signature: winner[0], colors: winner[1].colors }
      : point
  })

  let blocks = []
  let currentBlock = null
  for (const d of stabilized) {
    const colors = d.colors
    const isSame = currentBlock && 
                   d.signature === currentBlock.signature &&
                   d.sec - currentBlock.lastSec <= 2
    if (isSame) {
        currentBlock.endSec = d.sec + 1
        currentBlock.lastSec = d.sec
    } else {
        if (currentBlock) blocks.push(currentBlock)
        currentBlock = {
          startSec: d.sec,
          endSec: d.sec + 1,
          lastSec: d.sec,
          colors,
          signature: d.signature
        }
    }
  }
  if (currentBlock) blocks.push(currentBlock)
  return blocks
}

// 颜色稳定和分块只依赖后端轨迹；播放头移动时复用缓存，避免每次 timeupdate 重算。
const clothingBlocks = computed(() => {
  const result = {}
  for (const [profileId, history] of Object.entries(props.timelineData?.tracks || {})) {
    result[profileId] = {
      upper: getBlocks(history, 'upper_colors'),
      lower: getBlocks(history, 'lower_colors')
    }
  }
  return result
})

const getBlockStyle = (block) => ({
  left: `${block.startSec * props.pxPerSec}px`,
  width: `${(block.endSec - block.startSec) * props.pxPerSec}px`
})

const getTooltip = (colors) => colors.map(c => `${c.name}: ${(c.ratio * 100).toFixed(0)}%`).join('\n')

const resolveNodeAt = (e) => {
  if (!viewport.value) return { sec: 0, profileId: null, node: null, x: 0 }
  const rect = viewport.value.getBoundingClientRect()
  const x = e.clientX - rect.left + viewport.value.scrollLeft - sidebarWidth.value
  const sec = x / props.pxPerSec
  const targetSecInt = Math.floor(sec)
  
  const y = e.clientY - rect.top
  const trackGap = 20
  const trackIdx = Math.floor(y / (trackHeight + trackGap))
  
  let profileId = null
  let node = null
  
  if (props.timelineData?.tracks) {
    const tracks = Object.entries(props.timelineData.tracks)
    if (trackIdx >= 0 && trackIdx < tracks.length) {
      profileId = tracks[trackIdx][0]
      node = tracks[trackIdx][1].find(h => h.sec === targetSecInt)
    }
  }
  return { sec, profileId, node, x }
}

const onTimelineClick = (e) => {
  if (hasMoved) return
  const info = resolveNodeAt(e)
  if (!info || info.x < 0) return
  
  emit('click-sec', { 
    sec: Math.max(0, info.sec), 
    profileId: info.profileId, 
    node: info.node 
  })
}

function startResize(e, idx, side) {
  console.log('startResize', idx, side)
  isResizing.value = true
  resizingIdx.value = idx
  resizingSide.value = side
}

function startPlayheadDrag(e) {
  isDraggingPlayhead.value = true
  const info = resolveNodeAt(e)
  emit('click-sec', { 
    sec: Math.max(0, info.sec), 
    profileId: info.profileId, 
    node: info.node 
  })
}

function startDrag(e) {
  const rect = viewport.value.getBoundingClientRect()
  const x = e.clientX - rect.left + viewport.value.scrollLeft - sidebarWidth.value
  
  if (e.shiftKey) {
    isSelecting.value = true
    selectionStart.value = Math.max(0, x / props.pxPerSec)
    selectionEnd.value = selectionStart.value
    return
  }
  
  isDragging = true
  hasMoved = false
  startX = e.pageX - viewport.value.offsetLeft
  initialScrollLeft = viewport.value.scrollLeft
}

function onDrag(e) {
  const rect = viewport.value.getBoundingClientRect()
  const x = e.clientX - rect.left + viewport.value.scrollLeft - sidebarWidth.value
  const currentTime = Math.max(0, x / props.pxPerSec)

  if (isSelecting.value) {
    selectionEnd.value = currentTime
    return
  }

  if (isDraggingPlayhead.value) {
    const info = resolveNodeAt(e)
    emit('click-sec', { 
      sec: currentTime, 
      profileId: info.profileId, 
      node: info.node 
    })
    return
  }

  if (isResizing.value) {
    const idx = resizingIdx.value
    const seg = { ...props.selectedSegments[idx] }
    
    let targetTime = currentTime
    const snapThreshold = 10 / props.pxPerSec
    if (Math.abs(targetTime - props.playheadPos) < snapThreshold) {
      targetTime = props.playheadPos
    }

    if (resizingSide.value === 'left') {
      seg.start = Math.min(targetTime, seg.end - 0.1)
    } else {
      seg.end = Math.max(targetTime, seg.start + 0.1)
    }
    emit('update-segment', { idx, segment: seg })
    return
  }
  
  if (!isDragging) return
  const walk = (e.pageX - viewport.value.offsetLeft - startX) * 1.2
  if (Math.abs(e.pageX - viewport.value.offsetLeft - startX) > 3) hasMoved = true
  viewport.value.scrollLeft = initialScrollLeft - walk
}

function endDrag() {
  if (isSelecting.value) {
    isSelecting.value = false
    const start = Math.min(selectionStart.value || 0, selectionEnd.value || 0)
    const end = Math.max(selectionStart.value || 0, selectionEnd.value || 0)
    if (end - start > 0.1) {
      emit('add-segment', { start, end })
    }
    selectionStart.value = null
    selectionEnd.value = null
  }
  isResizing.value = false
  isDraggingPlayhead.value = false
  isDragging = false
}

const handleWheel = (e) => {
  if (e.ctrlKey) {
    e.preventDefault()
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9
    
    // Calculate time under mouse to keep it centered during zoom
    if (viewport.value) {
      const rect = viewport.value.getBoundingClientRect()
      const xInViewport = e.clientX - rect.left - sidebarWidth.value
      const mouseTime = (Math.max(0, xInViewport) + viewport.value.scrollLeft) / props.pxPerSec
      zoom(zoomFactor, mouseTime)
    } else {
      zoom(zoomFactor)
    }
  } else if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
    viewport.value.scrollLeft += (e.deltaX || e.deltaY)
    syncScroll()
    e.preventDefault()
  }
}

const zoom = (factor, centerTime) => {
  const oldScale = props.pxPerSec
  const newScale = Math.min(100, Math.max(0.5, oldScale * factor))
  
  if (newScale === oldScale) return

  // If no centerTime provided, use the center of the viewport
  if (centerTime === undefined && viewport.value) {
    const rect = viewport.value.getBoundingClientRect()
    centerTime = (viewport.value.scrollLeft + (rect.width - sidebarWidth.value) / 2) / oldScale
  }

  const oldScrollLeft = viewport.value ? viewport.value.scrollLeft : 0
  
  emit('update:pxPerSec', newScale)

  nextTick(() => {
    if (viewport.value) {
      // Keep centerTime at the same visual position
      const newScrollLeft = oldScrollLeft + centerTime * (newScale - oldScale)
      viewport.value.scrollLeft = newScrollLeft
      syncScroll()
    }
  })
}

const onKeyDown = (e) => {
  const key = e.key.toLowerCase()
  if (key === 'i') {
    if (selectionEnd.value !== null) {
      const start = Math.min(props.playheadPos, selectionEnd.value)
      const end = Math.max(props.playheadPos, selectionEnd.value)
      if (end - start > 0.1) {
        emit('add-segment', { start, end })
      }
      selectionStart.value = null
      selectionEnd.value = null
    } else {
      selectionStart.value = props.playheadPos
    }
  } else if (key === 'o') {
    if (selectionStart.value !== null) {
      const start = Math.min(selectionStart.value, props.playheadPos)
      const end = Math.max(selectionStart.value, props.playheadPos)
      if (end - start > 0.1) {
        emit('add-segment', { start, end })
      }
      selectionStart.value = null
      selectionEnd.value = null
    } else {
      selectionEnd.value = props.playheadPos
    }
  } else if (key === 'escape') {
    selectionStart.value = null
    selectionEnd.value = null
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeyDown)
})
onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown)
})

defineExpose({ viewport })
</script>

<style scoped>
.timeline-tracks {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #252525;
  user-select: none;
  position: relative;
}

.timeline-header {
  height: 24px;
  background: #1a1a1a;
  position: relative;
  border-bottom: 1px solid #333;
  display: flex;
  overflow: hidden;
}

.sidebar-corner {
  background: #1a1a1a;
  border-right: 2px solid #666;
  flex-shrink: 0;
  position: sticky;
  left: 0;
  z-index: 110;
}

#ticks { height: 100%; position: relative; flex: 1; min-width: 0; }
.tick {
  position: absolute;
  border-left: 1px solid #555;
  height: 100%;
  top: 0;
  font-size: 12px;
  color: #ccc;
  padding-left: 4px;
  display: flex;
  align-items: flex-end;
  padding-bottom: 2px;
}

.timeline-viewport {
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;
  position: relative;
  cursor: grab;
}
.timeline-viewport:active { cursor: grabbing; }

#timeline-track { 
  position: relative; 
  min-height: 100%; 
}

.scroll-spacer {
  height: 40px;
  width: 100%;
}

.track-container {
  position: relative;
  margin-bottom: 10px;
  border-bottom: 1px solid #333;
  display: flex;
  width: 100%;
  min-width: max-content;
}

.timeline-tracks.resizing-sidebar { cursor: col-resize; }

.track-sidebar {
  position: sticky;
  left: 0;
  z-index: 100;
  background: #252525;
  border-right: 2px solid #666;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  user-select: none;
  flex-shrink: 0;
}

.sidebar-resizer {
  position: absolute;
  right: -4px;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: col-resize;
  z-index: 101;
}
.sidebar-resizer:hover {
  background: rgba(0, 210, 211, 0.3);
}

.profile-header {
  display: flex;
  align-items: center;
  padding: 0 4px;
  flex: 1;
  min-width: 0;
}
.profile-text {
  font-size: 13px;
  color: #00d2d3;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.track-row-labels {
  position: relative;
  width: 36px;
  height: 100%;
  pointer-events: none;
}
.row-label-item {
  font-size: 12px;
  color: #ddd;
  height: 24px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 4px;
  position: absolute;
}
.label-upper { top: 4px; }
.label-lower { top: 32px; }

.track-content {
  position: relative;
  flex: 1;
}

.color-row { position: absolute; width: 100%; height: 24px; display: flex; }
.upper-row { top: 4px; }
.lower-row { top: 32px; }

.color-block {
  position: absolute;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: opacity 0.2s;
}
.color-block:hover { opacity: 0.8; outline: 1px solid #fff; z-index: 5; }

#playhead {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #00d2d3;
  z-index: 30;
  cursor: col-resize;
  will-change: transform;
}
.playhead-hitzone {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -8px;
  right: -8px;
}
#playhead::after {
  content: '';
  position: absolute;
  top: 0;
  left: -4px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #00d2d3;
}

.selection-draft {
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(0, 210, 211, 0.2);
  border: 1px dashed #00d2d3;
  pointer-events: none;
  z-index: 5;
}

.selected-segment {
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(0, 210, 211, 0.4);
  border: 1px solid #00d2d3;
  z-index: 8;
  cursor: default;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding-bottom: 8px;
  gap: 4px;
  pointer-events: none;
}

.handle-left, .handle-right {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 10px;
  cursor: ew-resize;
  z-index: 25;
  pointer-events: auto;
}
.handle-left { left: -5px; }
.handle-right { right: -5px; }

.handle-left::after, .handle-right::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 2px;
  height: 16px;
  background: #fff;
  border-radius: 1px;
  box-shadow: 0 0 2px rgba(0,0,0,0.5);
  display: none;
}
.handle-left:hover::after,
.handle-right:hover::after {
  display: block;
}

.seg-label {
  font-size: 11px;
  color: #fff;
  background: rgba(0,0,0,0.5);
  padding: 0 4px;
  border-radius: 2px;
}
.delete-icon {
  opacity: 0.7;
  cursor: pointer;
  transition: opacity 0.2s, color 0.2s;
  pointer-events: auto;
}
.delete-icon:hover {
  opacity: 1;
  color: #ff5252;
}

#click-layer { position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 5; cursor: pointer; }

.highlight-mark {
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(255, 255, 0, 0.2);
  border-left: 1px solid rgba(255, 255, 0, 0.5);
  pointer-events: none;
  z-index: 1;
}

.zoom-controls {
  position: absolute;
  right: 20px;
  bottom: 10px;
  background: rgba(0,0,0,0.6);
  border-radius: 20px;
  display: flex;
  align-items: center;
  padding: 2px 8px;
  z-index: 100;
  gap: 4px;
}
.zoom-text { font-size: 11px; color: #ccc; min-width: 40px; text-align: center; }

/* Hide scrollbar */
.timeline-viewport::-webkit-scrollbar { height: 4px; }
.timeline-viewport::-webkit-scrollbar-thumb { background: #444; border-radius: 2px; }

/* Commodity Track Styles */
.commodity-track {
  height: 40px !important;
  background-color: rgba(255, 152, 0, 0.05);
  border-bottom: 2px solid #333;
}

.filterable-profile-header {
  gap: 2px;
}

.filterable-profile-header .profile-text {
  flex: 1;
}

.filter-count {
  flex: none;
  color: #888;
  font-size: 9px;
}

.track-filter-menu {
  width: 280px;
  max-height: 360px;
  background: #242424 !important;
  border: 1px solid #444;
}

.filter-menu-header {
  height: 40px;
  padding: 0 8px 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #ddd;
  font-size: 12px;
}

.filter-options {
  max-height: 300px;
  padding: 4px 0;
  overflow-y: auto;
}

.filter-option {
  min-height: 32px;
  padding: 0 10px 0 4px;
  display: flex;
  align-items: center;
  color: #ddd;
  cursor: pointer;
}

.filter-option:hover {
  background: rgba(255, 255, 255, 0.06);
}

.filter-option span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}

.commodity-block {
  position: absolute;
  top: 4px;
  height: 32px;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  overflow: hidden;
  transition: all 0.2s;
  z-index: 10;
}

.commodity-block:hover {
  filter: brightness(1.2);
  z-index: 12;
}

.comm-inner {
  width: 100%;
  height: 100%;
  padding: 0 4px;
  display: flex;
  align-items: center;
}

.comm-name {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}

:deep(.comm-tooltip) {
  background: #1e1e1e !important;
  border: 1px solid #444 !important;
  padding: 8px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5) !important;
  pointer-events: auto !important;
}

:deep(.comm-tooltip) * {
  user-select: text !important;
}

/* Placeholder Track */
.placeholder-track {
  height: 60px !important;
  background-color: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid #333;
}

.placeholder-bg {
  width: 100%;
  height: 100%;
  background-color: rgba(77, 182, 172, 0.03);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='40' viewBox='0 0 100 40'%3E%3Cpath d='M0 20 L2 10 L4 30 L6 15 L8 25 L10 5 L12 35 L14 18 L16 22 L18 10 L20 30 L22 15 L24 25 L26 5 L28 35 L30 18 L32 22 L34 10 L36 30 L38 15 L40 25 L42 5 L44 35 L46 18 L48 22 L50 10 L52 30 L54 15 L56 25 L58 5 L60 35 L62 18 L64 22 L66 10 L68 30 L70 15 L72 25 L74 5 L76 35 L78 18 L80 22 L82 10 L84 30 L86 15 L88 25 L90 5 L92 35 L94 18 L96 22 L98 10 L100 20' fill='none' stroke='%234db6ac' stroke-width='1' opacity='0.4'/%3E%3C/svg%3E");
  background-repeat: repeat-x;
  background-position: center;
  background-size: auto 60%;
}

/* Audio Track Styles */
.speaker-track {
  height: 44px !important;
  background-color: rgba(139, 195, 74, 0.04);
  border-bottom: 2px solid #333;
}

.speaker-track-title {
  color: #aed581;
}

.speaker-block {
  position: absolute;
  top: 5px;
  height: 34px;
  min-width: 2px;
  border: 1px solid;
  border-radius: 3px;
  padding: 0 5px;
  display: flex;
  align-items: center;
  gap: 5px;
  overflow: hidden;
  cursor: pointer;
  z-index: 10;
}

.speaker-block:hover {
  filter: brightness(1.25);
  z-index: 12;
}

.track-empty-state {
  position: sticky;
  left: 8px;
  width: max-content;
  height: 44px;
  display: flex;
  align-items: center;
  color: #9e9e9e;
  font-size: 11px;
  pointer-events: none;
}

.timeline-tooltip-card {
  width: min(430px, 80vw);
}

.tooltip-copy {
  min-width: 0;
  flex: 1;
}

.tooltip-filter-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.speaker-tooltip-card {
  min-width: 300px;
}

.speaker-tooltip-heading {
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 12px;
  gap: 3px;
}

.speaker-tooltip-heading > span {
  margin-left: auto;
  color: #aaa;
  font-family: monospace;
  font-size: 11px;
}

.speaker-tooltip-text {
  max-height: 96px;
  margin-top: 5px;
  overflow-y: auto;
  color: #ddd;
  font-size: 12px;
  line-height: 1.5;
  white-space: normal;
}

.commodity-empty-state {
  height: 40px;
}

.speaker-name {
  flex: none;
  font-size: 11px;
  font-weight: 700;
}

.speaker-block-inner {
  width: 100%;
  height: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.speaker-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  opacity: 0.9;
}

.audio-track {
  height: 64px !important;
  background-color: rgba(0, 188, 212, 0.05);
  border-bottom: 2px solid #333;
}

.metric-profile-header {
  align-items: center;
}

.metric-title-stack {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.metric-mean {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audio-mean {
  color: #80deea;
  font-size: 9px;
  line-height: 1.1;
  white-space: nowrap;
}

.speech-rate-track {
  height: 64px !important;
  background-color: rgba(126, 87, 194, 0.06);
  border-bottom: 2px solid #333;
}

.speech-rate-mean {
  color: #d1c4e9;
  font-size: 9px;
  line-height: 1.1;
  white-space: nowrap;
}

</style>
